// apps/admin-ui/src/pages/GovernancePage.tsx
// ALL RIGHTS RESERVED

/**
 * Governance Page
 *
 * Council voting interface for proposals.
 * Migrated from apps/csr — Layout wrapper removed (admin-ui App.tsx wraps),
 * governance access caching removed (use RBAC), tokens adapted.
 *
 * @module apps/admin-ui/pages/GovernancePage
 */

import { useState, useEffect, useCallback } from 'react';
import { CreateProposalModal } from '../components/governance/CreateProposalModal';
import { ProposalRowExpanded } from '../components/governance/ProposalRowExpanded';
import { 
  getProposals, 
  getCurrentMemberInfo, 
  getCouncilMembers,
  type Proposal,
  type CouncilMember,
  type CurrentMemberInfo,
} from '../api/governance';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

type StatusFilter = 'all' | 'draft' | 'voting' | 'passed' | 'rejected' | 'vetoed' | 'executed' | 'expired';
type TypeFilter = 'all' | 'parameter_change' | 'fee_adjustment' | 'member_add' | 'member_remove' | 'policy_update' | 'reserve_action' | 'compliance_update';

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function formatProposalType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'draft': return 'bg-gray-100 text-gray-700';
    case 'voting': return 'bg-blue-100 text-blue-700';
    case 'passed': return 'bg-green-100 text-green-700';
    case 'rejected': return 'bg-red-100 text-red-700';
    case 'vetoed': return 'bg-orange-100 text-orange-700';
    case 'executed': return 'bg-purple-100 text-purple-700';
    case 'expired': return 'bg-gray-100 text-gray-500';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function getTimeRemaining(votingEndsAt: string | null): string {
  if (!votingEndsAt) return '—';
  const end = new Date(votingEndsAt);
  const now = new Date();
  const diff = end.getTime() - now.getTime();
  
  if (diff <= 0) return 'Ended';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h`;
}

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export function GovernancePage() {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [members, setMembers] = useState<CouncilMember[]>([]);
  const [memberInfo, setMemberInfo] = useState<CurrentMemberInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  // Fetch initial data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [proposalsRes, memberInfoRes, councilRes] = await Promise.all([
        getProposals({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          type: typeFilter !== 'all' ? typeFilter : undefined,
        }),
        getCurrentMemberInfo(),
        getCouncilMembers(),
      ]);

      setProposals(proposalsRes.proposals);
      setMemberInfo(memberInfoRes);
      setMembers(councilRes.members);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load governance data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh proposals after action
  const handleRefresh = () => {
    fetchData();
  };

  // Toggle row expansion
  const handleRowClick = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Render loading/error states
  if (loading && proposals.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-content-secondary">Loading governance data...</div>
      </div>
    );
  }

  if (error && proposals.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-content">Governance</h1>
          <p className="text-sm text-content-secondary mt-1">
            Council voting and proposal management
          </p>
        </div>
        
        {memberInfo?.canCreate && (
          <button
            onClick={() => {
              console.log('New Proposal clicked, showCreateModal:', showCreateModal);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-brand-500 text-white rounded-md hover:bg-brand-500/90 transition-colors"
          >
            + New Proposal
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-content-secondary">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-3 py-1.5 border border-edge rounded-md bg-surface-subtle text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="voting">Voting</option>
            <option value="passed">Passed</option>
            <option value="executed">Executed</option>
            <option value="draft">Draft</option>
            <option value="rejected">Rejected</option>
            <option value="vetoed">Vetoed</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-content-secondary">Type:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            className="px-3 py-1.5 border border-edge rounded-md bg-surface-subtle text-sm"
          >
            <option value="all">All Types</option>
            <option value="parameter_change">Parameter Change</option>
            <option value="fee_adjustment">Fee Adjustment</option>
            <option value="member_add">Member Add</option>
            <option value="member_remove">Member Remove</option>
            <option value="policy_update">Policy Update</option>
            <option value="reserve_action">Reserve Action</option>
            <option value="compliance_update">Compliance Update</option>
          </select>
        </div>

        {loading && (
          <span className="text-sm text-content-muted ml-auto">Refreshing...</span>
        )}
      </div>

      {/* Proposals Table */}
      <div className="border border-edge rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-emphasis">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-content-secondary w-12">#</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-content-secondary">Title</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-content-secondary w-32">Type</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-content-secondary w-24">Status</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-content-secondary w-24">Votes</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-content-secondary w-24">Deadline</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-edge">
            {proposals.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-content-secondary">
                  No proposals found
                </td>
              </tr>
            ) : (
              proposals.map((proposal) => (
                <ProposalRow
                  key={proposal.id}
                  proposal={proposal}
                  members={members}
                  memberInfo={memberInfo}
                  isExpanded={expandedId === proposal.id}
                  onToggle={() => handleRowClick(proposal.id)}
                  onRefresh={handleRefresh}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Member Info */}
      {memberInfo && (
        <div className="mt-6 p-4 bg-surface-subtle rounded-lg border border-edge">
          <h3 className="text-sm font-medium text-content mb-2">Your Access</h3>
          <div className="flex items-center gap-4 text-sm text-content-secondary">
            {memberInfo.member ? (
              <>
                <span>Role: <span className="text-content capitalize">{memberInfo.member.role}</span></span>
                <span>•</span>
                <span>Can Vote: <span className="text-content">{memberInfo.canVote ? 'Yes' : 'No'}</span></span>
                <span>•</span>
                <span>Can Create: <span className="text-content">{memberInfo.canCreate ? 'Yes' : 'No'}</span></span>
                {memberInfo.canVeto && (
                  <>
                    <span>•</span>
                    <span className="text-orange-600">Can Veto</span>
                  </>
                )}
              </>
            ) : (
              <span>Viewing as: <span className="text-content capitalize">{memberInfo.appRole || 'observer'}</span></span>
            )}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateProposalModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleRefresh}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// PROPOSAL ROW
// ═══════════════════════════════════════════════════════

interface ProposalRowProps {
  proposal: Proposal;
  members: CouncilMember[];
  memberInfo: CurrentMemberInfo | null;
  isExpanded: boolean;
  onToggle: () => void;
  onRefresh: () => void;
}

function ProposalRow({ proposal, members, memberInfo, isExpanded, onToggle, onRefresh }: ProposalRowProps) {
  const voteTally = `${proposal.yesVotes}/${proposal.noVotes}/${proposal.vetoVotes}`;

  return (
    <>
      <tr 
        className="hover:bg-surface-subtle cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-3 text-sm text-content-muted">
          {proposal.proposalNumber}
        </td>
        <td className="px-4 py-3">
          <div className="text-sm font-medium text-content truncate max-w-md">
            {proposal.title}
          </div>
          <div className="text-xs text-content-muted">
            by {proposal.creatorName || 'Unknown'}
          </div>
        </td>
        <td className="px-4 py-3">
          <span className="text-xs text-content-secondary">
            {formatProposalType(proposal.proposalType)}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(proposal.status)}`}>
            {proposal.status}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-content-secondary font-mono">
          {voteTally}
        </td>
        <td className="px-4 py-3 text-sm text-content-secondary">
          {proposal.status === 'voting' ? getTimeRemaining(proposal.votingEndsAt) : '—'}
        </td>
        <td className="px-4 py-3">
          <svg 
            className={`w-4 h-4 text-content-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </td>
      </tr>
      
      {isExpanded && (
        <tr>
          <td colSpan={7} className="bg-surface-subtle border-t border-edge">
            <ProposalRowExpanded
              proposal={proposal}
              members={members}
              memberInfo={memberInfo}
              onRefresh={onRefresh}
            />
          </td>
        </tr>
      )}
    </>
  );
}
